import { Paperclip } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { getPleaStatusStyle } from '../constants';
import type { Attachment } from '../types';

interface AttachmentListProps {
  attachments: Attachment[];
  attachmentsLoading: boolean;
  attachmentsError: string | null;
}

/** Renders a plea's attachments with their filename and scan status (pending/approved/rejected). */
export function AttachmentList({ attachments, attachmentsLoading, attachmentsError }: AttachmentListProps) {
  if (attachmentsLoading) {
    return (
      <div className="space-y-2" role="status" aria-label="Carregando anexos">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-4 w-28" />
      </div>
    );
  }

  if (attachmentsError) {
    return <p className="text-sm text-slate-400">{attachmentsError}</p>;
  }

  if (attachments.length === 0) {
    return <p className="text-sm text-slate-400">Nenhum anexo enviado</p>;
  }

  return (
    <ul className="space-y-1.5">
      {attachments.map((attachment) => {
        const statusStyle = getPleaStatusStyle(attachment.scanStatus);
        return (
          <li key={attachment.uuid} className="flex items-center gap-2 text-sm text-slate-600">
            <Paperclip className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate">{attachment.originalFilename}</span>
            <span
              className={cn(
                'shrink-0 rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider',
                statusStyle.bg,
                statusStyle.text,
              )}
            >
              {statusStyle.label}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
